import React, { useState, useEffect, useRef } from "react";
import {
  faFacebook,
  faInstagram,
  faTwitter,
  faLinkedin,
  faPinterest,
} from "@fortawesome/free-brands-svg-icons";
import { faEnvelope } from "@fortawesome/free-regular-svg-icons";
import {
  faLocationDot,
  faPhone,
  faCheck,
  faChevronDown,
  faTimes,
  faPaperPlane,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CREATE_INQUIRY, GET_ALL_PRODUCTS } from "../utils/constant";
import API_CLIENT from "../lib/api-client";
import { toast } from "react-toastify";

/* ─── Contact details (synced with footer) ─────────────────────── */
const CONTACT_INFO = [
  {
    icon: faEnvelope,
    label: "Send us an Email",
    value: "growtextile15@gmail.com",
    href: "mailto:growtextile15@gmail.com",
    sub: "We reply within 24 hours",
  },
  {
    icon: faPhone,
    label: "Call Anytime",
    value: "+91 9876543210",
    href: "tel:+919876543210",
    sub: "Mon–Sat, 9 AM – 6 PM IST",
  },
  {
    icon: faLocationDot,
    label: "Visit Us",
    value: "123 Textile Street, Fashion District,\nAhmedabad, Gujarat – 380001",
    href: "https://maps.google.com",
    sub: "Fabric Most Trusted · EST. 2010",
  },
];

const SOCIALS = [
  { icon: faFacebook, href: "#", label: "Facebook" },
  { icon: faInstagram, href: "#", label: "Instagram" },
  { icon: faTwitter, href: "#", label: "Twitter" },
  { icon: faLinkedin, href: "#", label: "LinkedIn" },
  { icon: faPinterest, href: "#", label: "Pinterest" },
];

/* ════════════════════════════════════════════════════════════════ */
const ContactForm = () => {
  const [formData, setFormData] = useState({
    userName: "",
    emailId: "",
    subject: "",
    phoneNo: "",
    message: "",
    status: "pending",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [productSearch, setProductSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  /* ── fetch products ───── */
  useEffect(() => {
    API_CLIENT.get(GET_ALL_PRODUCTS)
      .then(res => { if (res.status === 200) setProducts(res.data.products); })
      .catch(() => { });
  }, []);

  /* ── close dropdown on outside click ── */
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setShowDropdown(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleChange = (e) =>
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const toggleProduct = (product) =>
    setSelectedProducts(prev =>
      prev.some(p => p._id === product._id)
        ? prev.filter(p => p._id !== product._id)
        : [...prev, product]
    );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const res = await API_CLIENT.post(CREATE_INQUIRY, {
        ...formData,
        products: selectedProducts.map(p => p._id),
      });
      if (res.status === 201) {
        setSubmitted(true);
        toast.success("Inquiry submitted successfully!");
        setFormData({ userName: "", emailId: "", subject: "", phoneNo: "", message: "", status: "pending" });
        setSelectedProducts([]);
        setTimeout(() => setSubmitted(false), 5000);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to submit. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const filtered = products.filter(p =>
    p.name.toLowerCase().includes(productSearch.toLowerCase())
  );

  /* ════════════════════════════════════════════════════════════════
     RENDER
  ════════════════════════════════════════════════════════════════ */
  return (
    <div className="bg-neutral-100 font-sans text-neutral-800">

      {/* ── Hero banner ─────────────────────────────────────────── */}
      <section className="relative bg-teal-700 overflow-hidden py-20">
        {/* weave texture */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "repeating-linear-gradient(0deg,#fff 0,#fff 1px,transparent 1px,transparent 24px)," +
              "repeating-linear-gradient(90deg,#fff 0,#fff 1px,transparent 1px,transparent 24px)",
          }}
        />
        <div className="container mx-auto px-6 relative z-10">
          <p className="text-teal-300 font-medium tracking-widest uppercase text-sm mb-4">
            Get In Touch
          </p>
          <h1 className="text-4xl md:text-6xl font-light text-white leading-tight mb-4">
            Let's Weave a<br />Conversation
          </h1>
          <div className="w-16 h-0.5 bg-teal-400 mb-6" />
          <p className="text-teal-100 text-lg max-w-xl leading-relaxed">
            Have a product inquiry, need a custom fabric order, or want to partner with us?
            We'd love to hear from you.
          </p>
        </div>
        {/* decorative arc */}
        <div className="absolute -bottom-px left-0 right-0 overflow-hidden leading-none">
          <svg viewBox="0 0 1200 60" preserveAspectRatio="none" className="w-full h-14 fill-neutral-100">
            <path d="M0,60 C300,0 900,0 1200,60 L1200,60 L0,60 Z" />
          </svg>
        </div>
      </section>

      {/* ── Main content: Form + Side panel ─────────────────────── */}
      <section className="py-10 pb-24 bg-neutral-100">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">

            {/* ══ FORM (3/5) ══════════════════════════════════════ */}
            <div className="lg:col-span-3 bg-white border border-neutral-200 p-8 md:p-10">
              <p className="text-teal-600 font-medium tracking-widest uppercase text-xs mb-3">
                Inquiry Form
              </p>
              <h2 className="text-3xl font-light text-neutral-900 mb-1">
                Write Us Something
              </h2>
              <div className="w-10 h-0.5 bg-teal-500 mb-8" />

              {/* Success state */}
              {submitted && (
                <div className="mb-6 flex items-center gap-3 bg-teal-50 border border-teal-200 px-5 py-4 rounded-sm">
                  <div className="w-8 h-8 rounded-full bg-teal-600 flex items-center justify-center flex-shrink-0">
                    <FontAwesomeIcon icon={faCheck} className="text-white text-sm" />
                  </div>
                  <p className="text-teal-800 text-sm font-medium">
                    Your inquiry has been submitted! We'll get back to you within 24 hours.
                  </p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Name + Email */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <FormField label="Your Name" required>
                    <input
                      type="text" name="userName" value={formData.userName}
                      onChange={handleChange} placeholder="John Doe" required
                      className="contact-input"
                    />
                  </FormField>
                  <FormField label="Email Address" required>
                    <input
                      type="email" name="emailId" value={formData.emailId}
                      onChange={handleChange} placeholder="john@example.com" required
                      className="contact-input"
                    />
                  </FormField>
                </div>

                {/* Subject + Phone */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <FormField label="Subject" required>
                    <input
                      type="text" name="subject" value={formData.subject}
                      onChange={handleChange} placeholder="Fabric inquiry…" required
                      className="contact-input"
                    />
                  </FormField>
                  <FormField label="Contact Number" required>
                    <input
                      type="tel" name="phoneNo" value={formData.phoneNo}
                      onChange={handleChange} placeholder="+91 98765 43210" required
                      className="contact-input"
                    />
                  </FormField>
                </div>

                {/* Product multi-select */}
                <FormField label="Products of Interest">
                  <div className="relative" ref={dropdownRef}>
                    <div
                      className="contact-input flex flex-wrap gap-2 cursor-pointer min-h-[46px] items-center"
                      onClick={() => setShowDropdown(s => !s)}
                    >
                      {selectedProducts.length === 0 && productSearch === "" && (
                        <span className="text-neutral-400 text-sm select-none">
                          Search & select products…
                        </span>
                      )}
                      {selectedProducts.map(p => (
                        <span
                          key={p._id}
                          className="inline-flex items-center gap-1 bg-teal-50 text-teal-700 border border-teal-200 text-xs px-2 py-1 rounded-sm"
                        >
                          {p.name}
                          <button
                            type="button"
                            className="text-teal-500 hover:text-teal-800 ml-0.5"
                            onClick={e => { e.stopPropagation(); toggleProduct(p); }}
                          >
                            <FontAwesomeIcon icon={faTimes} className="text-[10px]" />
                          </button>
                        </span>
                      ))}
                      <input
                        type="text"
                        className="flex-1 min-w-[100px] outline-none bg-transparent text-sm text-neutral-700 placeholder-neutral-400"
                        placeholder={selectedProducts.length ? "" : ""}
                        value={productSearch}
                        onChange={e => { setProductSearch(e.target.value); setShowDropdown(true); }}
                        onFocus={() => setShowDropdown(true)}
                        onClick={e => e.stopPropagation()}
                      />
                      <FontAwesomeIcon
                        icon={faChevronDown}
                        className={`text-neutral-400 text-xs ml-auto transition-transform duration-200 ${showDropdown ? "rotate-180" : ""}`}
                      />
                    </div>

                    {showDropdown && (
                      <div className="absolute z-20 w-full top-full mt-1 bg-white border border-neutral-200 shadow-xl max-h-52 overflow-y-auto">
                        {filtered.length === 0 ? (
                          <p className="px-4 py-3 text-sm text-neutral-400">No products found</p>
                        ) : filtered.map(p => {
                          const selected = selectedProducts.some(s => s._id === p._id);
                          return (
                            <div
                              key={p._id}
                              onClick={() => toggleProduct(p)}
                              className={`flex items-center gap-3 px-4 py-3 cursor-pointer text-sm transition-colors duration-150
                                ${selected ? "bg-teal-50 text-teal-700" : "hover:bg-neutral-50 text-neutral-700"}`}
                            >
                              <div className={`w-4 h-4 border flex items-center justify-center flex-shrink-0 transition-colors
                                ${selected ? "bg-teal-600 border-teal-600" : "border-neutral-300"}`}>
                                {selected && <FontAwesomeIcon icon={faCheck} className="text-white text-[9px]" />}
                              </div>
                              {p.name}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </FormField>

                {/* Message */}
                <FormField label="Message" required>
                  <textarea
                    name="message" value={formData.message} onChange={handleChange}
                    placeholder="Describe your requirement — fabric type, quantity, customization needs…"
                    rows={5} required
                    className="contact-input resize-none"
                  />
                </FormField>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="inline-flex items-center gap-3 px-8 py-3.5 bg-teal-600 text-white text-sm font-medium tracking-wide hover:bg-teal-700 transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Sending…
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faPaperPlane} />
                      Send Inquiry
                    </>
                  )}
                </button>
              </form>

              {/* inline style for contact inputs */}
              <style>{`
                .contact-input {
                  width: 100%;
                  padding: 11px 14px;
                  border: 1px solid #e5e7eb;
                  background: #fff;
                  font-size: .875rem;
                  color: #111827;
                  outline: none;
                  transition: border-color .2s, box-shadow .2s;
                  border-radius: 0;
                }
                .contact-input:hover { border-color: #6b7280; }
                .contact-input:focus { border-color: #0d9488; box-shadow: 0 0 0 3px rgba(13,148,136,.1); }
                .contact-input::placeholder { color: #9ca3af; }
              `}</style>
            </div>

            {/* ══ SIDEBAR (2/5) ═══════════════════════════════════ */}
            <div className="lg:col-span-2 flex flex-col gap-6">

              {/* Dark info panel */}
              <div className="bg-teal-900 text-white p-8">
                <p className="text-teal-400 text-xs font-semibold tracking-widest uppercase mb-2">
                  Contact
                </p>
                <h3 className="text-2xl font-light mb-1">Our Details</h3>
                <div className="w-8 h-0.5 bg-teal-500 mb-8" />

                <div className="space-y-7">
                  {CONTACT_INFO.map((item, i) => (
                    <a
                      key={i}
                      href={item.href}
                      target={item.href.startsWith("http") ? "_blank" : undefined}
                      rel="noreferrer"
                      className="group flex items-start gap-4"
                    >
                      <div className="w-10 h-10 bg-teal-800 flex items-center justify-center flex-shrink-0 group-hover:bg-teal-600 transition-colors duration-300">
                        <FontAwesomeIcon icon={item.icon} className="text-teal-300 text-sm group-hover:text-white transition-colors" />
                      </div>
                      <div>
                        <p className="text-xs text-teal-400 tracking-widest uppercase mb-0.5">{item.label}</p>
                        <p className="text-white text-sm leading-snug whitespace-pre-line">{item.value}</p>
                      </div>
                    </a>
                  ))}
                </div>

                {/* Social links */}
                <div className="mt-10 pt-8 border-t border-teal-800">
                  <p className="text-xs text-teal-400 tracking-widest uppercase mb-4">Follow Us</p>
                  <div className="flex gap-3 flex-wrap">
                    {SOCIALS.map((s, i) => (
                      <a
                        key={i}
                        href={s.href}
                        aria-label={s.label}
                        className="w-9 h-9 border border-teal-700 flex items-center justify-center text-teal-400 hover:bg-teal-600 hover:border-teal-600 hover:text-white transition-all duration-300 text-sm"
                      >
                        <FontAwesomeIcon icon={s.icon} />
                      </a>
                    ))}
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* ── Map placeholder ──────────────────────────────────────── */}
      <section className="bg-white border-t border-neutral-200">
        <div className="container mx-auto px-6 py-12">
          <div className="max-w-2xl mx-auto mb-8 text-center">
            <p className="text-teal-600 font-medium tracking-widest uppercase text-xs mb-3">Find Us</p>
            <h2 className="text-2xl md:text-3xl font-light text-neutral-900">
              123 Textile Street, Ahmedabad
            </h2>
            <div className="w-10 h-0.5 bg-teal-500 mx-auto mt-3" />
          </div>
          <div className="max-w-5xl mx-auto overflow-hidden border border-neutral-200 shadow-sm" style={{ height: 340 }}>
            <iframe
              title="GrowTextile Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3671.879099865689!2d72.5713748!3d23.022505!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x395e848aba5bd449%3A0x4fcedd11614f6516!2sAhmedabad%2C%20Gujarat!5e0!3m2!1sen!2sin!4v1690000000000"
              width="100%"
              height="340"
              style={{ border: 0, display: "block" }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>

    </div>
  );
};

/* ── Tiny helper ─────────────────────────────────────────────────── */
const FormField = ({ label, required, children }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-xs font-semibold tracking-widest uppercase text-neutral-500">
      {label}
      {required && <span className="text-teal-500 ml-1">*</span>}
    </label>
    {children}
  </div>
);

export default ContactForm;
